// =============================================================================
// PipelineStepper — Source: governance_pipeline (10 étapes, aucun hardcode)
// Étape 10 'rejet_abandon' affichée comme bannière latérale rouge distincte.
// =============================================================================
import { Check, XCircle, Loader2 } from "lucide-react";
import usePipeline from "../../../hooks/usePipeline";

// Mapping nom Tailwind (governance_pipeline.steps[].color) → hex utilisé dans les bulles.
// L'étape courante (active) utilise cette couleur au lieu du neutre "var(--oh-text)".
const TW_COLOR_TO_HEX = {
  slate: "var(--oh-stat-slate-text)", gray: "var(--oh-stat-slate-text)", zinc: "var(--oh-stat-slate-text)", stone: "var(--oh-stat-slate-text)",
  red: "var(--oh-stat-red-text)", orange: "var(--oh-stat-orange-text)", amber: "var(--oh-stat-amber-text)", yellow: "var(--oh-stat-yellow-text)",
  lime: "#84cc16", green: "var(--oh-stat-green-text)", emerald: "#10b981", teal: "#14b8a6",
  cyan: "var(--oh-stat-cyan-text)", sky: "#0ea5e9", blue: "var(--oh-stat-blue-text)", indigo: "#6366f1",
  violet: "#8b5cf6", purple: "var(--oh-stat-purple-text)", fuchsia: "#d946ef", pink: "#ec4899",
  rose: "#f43f5e",
};
const colorForStep = (step, fallback = "var(--oh-stat-slate-text)") =>
  TW_COLOR_TO_HEX[step?.color] || fallback;

/* ------------------------------------------------------------------ */
/*  Helpers purs (acceptent pipelineDoc explicite — utilisables hors React) */
/* ------------------------------------------------------------------ */

/** Legacy: "statut_amo_sheet" ou "statut_amo" = "Abandonné". */
export function isAbandonne(dossier) {
  return (
    dossier?.statut_amo_sheet === "Abandonné" ||
    dossier?.statut_amo === "Abandonné"
  );
}

/**
 * Résout le code d'étape cible à partir :
 * - du champ canonique workflow_data.tef.pipeline_status (priorité),
 * - sinon du statut legacy (top-level).
 *
 * @param {string} statut - statut legacy top-level
 * @param {object} dossier - objet dossier complet
 * @param {object} pipelineDoc - document governance_pipeline (steps + legacy_mapping)
 * @returns {string} code cible (cree, planifie, ..., rejet_abandon)
 */
export function resolveStepCode(statut, dossier, pipelineDoc) {
  if (isAbandonne(dossier)) return "rejet_abandon";
  if (!pipelineDoc) return null;
  const mapping = pipelineDoc.legacy_mapping || {};
  const stepCodes = new Set((pipelineDoc.steps || []).map((s) => s.code));
  // Priorité workflow_data.tef.pipeline_status
  const wdTef = (dossier?.workflow_data || {}).tef || {};
  const src = wdTef.pipeline_status || statut;
  if (!src) return "cree";
  if (stepCodes.has(src)) return src; // déjà un code cible
  return mapping[src] || "cree";
}

/** Renvoie l'ordre (1..N) ou 99 pour l'étape latérale rejet_abandon. */
export function resolveStepOrder(statut, dossier, pipelineDoc) {
  const code = resolveStepCode(statut, dossier, pipelineDoc);
  if (code === "rejet_abandon") return 99;
  const step = (pipelineDoc?.steps || []).find((s) => s.code === code);
  return step?.order || 1;
}

/* ------------------------------------------------------------------ */
/*  Backward-compat wrappers (utilisés par VueGlobale & DossierVisiteurView) */
/* ------------------------------------------------------------------ */

// Ces wrappers nécessitent un pipelineDoc : les callers doivent passer
// le résultat de usePipeline().pipeline. Si absent, fallback "cree"/1.
export function getPipelineStep(statut, dossier, pipelineDoc = null) {
  return resolveStepCode(statut, dossier, pipelineDoc) || "cree";
}

export function getPipelineOrder(statut, dossier, pipelineDoc = null) {
  return resolveStepOrder(statut, dossier, pipelineDoc);
}

/* ------------------------------------------------------------------ */
/*  Composant principal                                                */
/* ------------------------------------------------------------------ */

export function PipelineStepper({ statut, compact = false, onStepClick, dossier = null }) {
  const { pipeline, loading } = usePipeline();

  if (loading || !pipeline) {
    return (
      <div className="flex items-center gap-2 py-1" data-testid="pipeline-stepper-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Chargement du pipeline…</span>
      </div>
    );
  }

  // Étapes linéaires (1..N-1) vs étape latérale rejet_abandon (is_side)
  const linearSteps = (pipeline.steps || []).filter((s) => !s.is_side);
  const sideStep = (pipeline.steps || []).find((s) => s.is_side);

  const currentCode = resolveStepCode(statut, dossier, pipeline);
  const isRejected = currentCode === "rejet_abandon";
  const currentIdx = isRejected ? -1 : linearSteps.findIndex((s) => s.code === currentCode);

  return (
    <div
      className="oh-pipeline-stepper flex items-start gap-0 overflow-x-auto no-scrollbar py-1"
      data-testid="pipeline-stepper"
      data-current-code={currentCode}
    >
      {/* Bannière étape latérale (Rejeté/Abandonné) */}
      {isRejected && sideStep && (
        <div
          className="flex items-center gap-2 px-3 py-1 rounded-full mr-3 flex-shrink-0"
          style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)" }}
          data-testid="pipeline-side-step-active"
        >
          <XCircle className="w-3.5 h-3.5 text-[var(--oh-error-text)]" />
          <span className="text-xs font-medium text-[var(--oh-error-text)]">{sideStep.label}</span>
        </div>
      )}

      {/* Étapes linéaires */}
      {linearSteps.map((step, idx) => {
        const isCompleted = !isRejected && idx < currentIdx;
        const isCurrent = !isRejected && idx === currentIdx;
        const isClickable = !!onStepClick;
        const stepShort = step.short || step.label;
        const stepColor = colorForStep(step);

        return (
          <div key={step.code} className="flex items-start flex-shrink-0">
            {/* Colonne étape : bulle (hauteur fixe) + libellé (min-height 2 lignes).
                La bulle est le bouton lui-même (hauteur fixée par width/height),
                le libellé est un sibling en-dessous → la hauteur du libellé ne
                peut jamais faire décrocher la bulle de la ligne horizontale. */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                className={`flex items-center justify-center rounded-full transition-all flex-shrink-0 ${isClickable ? "cursor-pointer" : "cursor-default"}`}
                onClick={() => isClickable && onStepClick(step.code)}
                data-testid={`step-${step.code}`}
                title={step.label}
                aria-label={step.label}
                disabled={!isClickable}
                style={{
                  width: compact ? 22 : 28,
                  height: compact ? 22 : 28,
                  background: isCompleted ? stepColor : isCurrent ? stepColor : "transparent",
                  border: !isCompleted && !isCurrent ? "2px solid var(--oh-border)" : `2px solid ${isCurrent ? stepColor : "transparent"}`,
                  color: isCompleted || isCurrent ? "#ffffff" : "var(--oh-text-muted)",
                  boxShadow: isCurrent ? `0 0 0 3px ${stepColor}33` : "none",
                }}
              >
                {isCompleted ? (
                  <Check style={{ width: 12, height: 12 }} />
                ) : (
                  <span style={{ fontSize: compact ? 9 : 11, fontWeight: 700 }}>{step.order}</span>
                )}
              </button>
              <span
                className="oh-step-label mt-0.5 text-center leading-tight block"
                style={{
                  fontSize: compact ? 9 : 10,
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent
                    ? stepColor
                    : isCompleted
                    ? stepColor
                    : "var(--oh-text-muted)",
                  /* Réserve l'espace de 2 lignes (mode full desktop) :
                     empêche les étapes à 1 ligne de remonter leurs voisines.
                     2.5em = 2 × (font-size 10px × leading-tight 1.25) = 25px. */
                  minHeight: compact ? undefined : "2.5em",
                }}
              >
                {/* Libellé RESPONSIVE indépendant de `compact` (qui ne pilote
                    plus que la taille bulle/police) : libellé complet sur
                    conteneur large, abréviation + tooltip sinon — bascule par
                    @container (index.css). Les 3 usages dossier passent `compact`,
                    donc sans ce découplage le mode complet ne s'affichait jamais. */}
                <span className="oh-step-label-full">{step.label}</span>
                <span className="oh-step-label-short">{stepShort}</span>
              </span>
            </div>

            {idx < linearSteps.length - 1 && (
              <div
                className="mx-0.5 sm:mx-1"
                style={{
                  width: compact ? 12 : 16,
                  height: 2,
                  /* Descend le connecteur au centre vertical de la bulle
                     (rayon − moitié du trait) : toujours aligné sur la rangée
                     de bulles, quelle que soit la hauteur du libellé en dessous. */
                  marginTop: compact ? 10 : 13,
                  background: !isRejected && idx < currentIdx ? colorForStep(linearSteps[idx]) : "var(--oh-border)",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
